import { useState, useCallback, useRef } from 'preact/hooks';

interface AsyncAction<T, A extends unknown[]> {
  execute: (...args: A) => Promise<T | undefined>;
  loading: boolean;
  error: string | null;
  result: T | null;
}

export function useAsyncAction<T, A extends unknown[] = []>(
  action: (...args: A) => Promise<T>,
): AsyncAction<T, A> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<T | null>(null);
  const actionRef = useRef(action);
  actionRef.current = action;

  const execute = useCallback(async (...args: A) => {
    setLoading(true);
    setError(null);
    try {
      const res = await actionRef.current(...args);
      setResult(res);
      return res;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, result };
}
